// Workflow types for the shared types package

import type { AuthTokenSource } from './external/mcp-write-confirmation.js';
import {
  ApBillAlertType,
  ApBillType,
  PaymentMethodType,
  type RowStatus,
  type WorkflowConditionLineQuantifier,
  WorkflowRunOutcome,
} from './generated/enums.js';
import type { WorkflowOriginInput, WorkflowOriginResponse } from './wf-origin.js';
import {
  AutoApproveActionConfigSchema,
  ContractReviewEnqueueIntakeActionConfigSchema,
  RequestApprovalActionConfigSchema,
  RequirePurchaseOrderActionConfigSchema,
  SendNotificationActionConfigSchema,
  SetCostCenterActionConfigSchema,
  SetCustomSegmentActionConfigSchema,
  SetDepartmentActionConfigSchema,
  SetGlAccountActionConfigSchema,
  SetManagerialAccountActionConfigSchema,
  SetOriginBankAccountActionConfigSchema,
  SubmitForApprovalActionConfigSchema,
} from './wf-schemas.js';

/**
 * Dynamic segment condition field name pattern used in workflow conditions.
 * Matches strings like: `segment[uuid]`
 */
export type SegmentConditionFieldName = `segment[${string}]`;

/**
 * Bill workflow condition operators supported by backend evaluation.
 */
export const BillWorkflowConditionOperator = {
  equals: 'equals',
  not_equals: 'not_equals',
  contains: 'contains',
  greater_than: 'greater_than',
  less_than: 'less_than',
  greater_or_equal: 'greater_or_equal',
  less_or_equal: 'less_or_equal',
  in: 'in',
  not_in: 'not_in',
  is_null: 'is_null',
  is_not_null: 'is_not_null',
  /**
   * List-valued state (a fact such as the open alert types of a bill): "has any of" matches when at
   * least one state element is in the condition list; "has none of" matches when no state element
   * is (an empty state list matches). Neither is `in` / `not_in`: those test one scalar against a list.
   */
  has_any_of: 'has_any_of',
  has_none_of: 'has_none_of',
  /**
   * Profile-valued state (the withholding or the credit profile of a bill, ALC-4563): matches when
   * the bill decided the same profile as the condition value, compared as exact text and booleans
   * (a rate is never a float). A bill with no profile recorded never matches.
   */
  same_profile_as: 'same_profile_as',
  /**
   * Allocation-valued state (the targets a bill is allocated to): matches when every target of the
   * bill, in each dimension the condition names, is inside the condition's set. Percentages and
   * amounts are not compared.
   */
  all_within: 'all_within',
  /**
   * ALC-4658 (D50) split modes of the allocation targets fact (the value and the rules live in
   * `wf-fact-values.ts`, `ALLOCATION_TARGETS_OPERATORS`):
   * - `same_targets` "Rateio entre estes": the bill uses exactly the listed targets, any percent;
   * - `exact_split` "Exatamente estes %": a bill-level simple split with exactly these percents;
   * - `any_within` "Pelo menos um destes": at least one target of the bill is listed.
   */
  same_targets: 'same_targets',
  exact_split: 'exact_split',
  any_within: 'any_within',
} as const;
export type BillWorkflowConditionOperator =
  (typeof BillWorkflowConditionOperator)[keyof typeof BillWorkflowConditionOperator];

export const BILL_WORKFLOW_CONDITION_OPERATOR_VALUES = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.contains,
  BillWorkflowConditionOperator.greater_than,
  BillWorkflowConditionOperator.less_than,
  BillWorkflowConditionOperator.greater_or_equal,
  BillWorkflowConditionOperator.less_or_equal,
  BillWorkflowConditionOperator.in,
  BillWorkflowConditionOperator.not_in,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
  BillWorkflowConditionOperator.has_any_of,
  BillWorkflowConditionOperator.has_none_of,
  BillWorkflowConditionOperator.same_profile_as,
  BillWorkflowConditionOperator.all_within,
  BillWorkflowConditionOperator.same_targets,
  BillWorkflowConditionOperator.exact_split,
  BillWorkflowConditionOperator.any_within,
] as const satisfies readonly BillWorkflowConditionOperator[];

export type BillWorkflowConditionScope = 'BILL' | 'LINE_ITEM';
// prettier-ignore
export type BillWorkflowConditionValueKind =
  | 'entity'
  | 'number'
  | 'text'
  | 'date'
  | 'enum'
  | 'boolean'
  // ALC-4563: facts whose value is picked or built in a dedicated editor.
  | 'service_type'
  | 'withholding_profile'
  | 'credit_profile'
  | 'allocation_targets';

export const BILL_ENTITY_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.in,
  BillWorkflowConditionOperator.not_in,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

const BILL_TEXT_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.contains,
  BillWorkflowConditionOperator.in,
  BillWorkflowConditionOperator.not_in,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

const BILL_NUMBER_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.greater_than,
  BillWorkflowConditionOperator.less_than,
  BillWorkflowConditionOperator.greater_or_equal,
  BillWorkflowConditionOperator.less_or_equal,
  BillWorkflowConditionOperator.in,
  BillWorkflowConditionOperator.not_in,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

const BILL_DATE_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.greater_than,
  BillWorkflowConditionOperator.less_than,
  BillWorkflowConditionOperator.greater_or_equal,
  BillWorkflowConditionOperator.less_or_equal,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

const BILL_TYPE_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.not_equals,
  BillWorkflowConditionOperator.in,
  BillWorkflowConditionOperator.not_in,
] as const satisfies readonly BillWorkflowConditionOperator[];

const BILL_PAYMENT_METHOD_OPERATORS = [
  ...BILL_TYPE_OPERATORS,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

/** A boolean fact is compared with `equals` against `true` or `false`; there is no `not_equals`. */
export const BILL_BOOLEAN_OPERATORS = [
  BillWorkflowConditionOperator.equals,
] as const satisfies readonly BillWorkflowConditionOperator[];

/** A boolean fact whose state can be unknown (null) also offers the null checks. */
export const BILL_NULLABLE_BOOLEAN_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.is_null,
  BillWorkflowConditionOperator.is_not_null,
] as const satisfies readonly BillWorkflowConditionOperator[];

/** A fact whose value is a profile (withholding, credit) is compared with "same profile as" only. */
export const BILL_PROFILE_OPERATORS = [
  BillWorkflowConditionOperator.same_profile_as,
] as const satisfies readonly BillWorkflowConditionOperator[];

/**
 * The allocation targets fact is tested with one of the split modes (ALC-4658, D50). The order is the
 * order an editor offers them: "Rateio entre estes" first (the default of a new condition).
 * `all_within` stays last for the rules that use it today (P55); editors do not offer it for a new
 * condition.
 */
export const BILL_ALLOCATION_TARGET_OPERATORS = [
  BillWorkflowConditionOperator.same_targets,
  BillWorkflowConditionOperator.exact_split,
  BillWorkflowConditionOperator.any_within,
  BillWorkflowConditionOperator.all_within,
] as const satisfies readonly BillWorkflowConditionOperator[];

/**
 * Operators a fact still evaluates (and the API still accepts on save, so an MCP or API edit of a
 * saved rule keeps working) but editors and prefills no longer offer for a NEW condition:
 * `all_within` of the allocation targets fact (ALC-4658, P55). An editor offers one only to the
 * condition that was saved with it.
 */
export const RETIRED_FACT_OPERATORS = [
  BillWorkflowConditionOperator.all_within,
] as const satisfies readonly BillWorkflowConditionOperator[];

/** True for an operator of {@link RETIRED_FACT_OPERATORS}. */
export function isRetiredFactOperator(operator: unknown): boolean {
  return (RETIRED_FACT_OPERATORS as readonly unknown[]).includes(operator);
}

/** True for an operator of the allocation targets fact (the split modes and `all_within`). */
export function isBillAllocationTargetOperator(operator: unknown): boolean {
  return (BILL_ALLOCATION_TARGET_OPERATORS as readonly unknown[]).includes(operator);
}

/** The fiscal service type fact is one id: equal to a service type, or one of several. */
export const BILL_SERVICE_TYPE_OPERATORS = [
  BillWorkflowConditionOperator.equals,
  BillWorkflowConditionOperator.in,
] as const satisfies readonly BillWorkflowConditionOperator[];

/** A list-valued fact (state is an array of enum values) is tested with the list operators only. */
export const BILL_LIST_OPERATORS = [
  BillWorkflowConditionOperator.has_any_of,
  BillWorkflowConditionOperator.has_none_of,
] as const satisfies readonly BillWorkflowConditionOperator[];

export const BILL_WORKFLOW_BILL_LEVEL_FIELDS = [
  'vendor',
  'amount',
  'bill_issue_date',
  'bill_type',
  'payment_method',
  'cost_center',
  'department',
  'gl_account',
  'managerial_account',
] as const;

export const BILL_WORKFLOW_LINE_LEVEL_FIELDS = [
  'line_item_id',
  'line_description',
  'line_amount',
  'cfop_code',
] as const;

export const BILL_WORKFLOW_STATIC_CONDITION_FIELDS = [
  ...BILL_WORKFLOW_BILL_LEVEL_FIELDS,
  ...BILL_WORKFLOW_LINE_LEVEL_FIELDS,
] as const;

export type BillWorkflowStaticConditionField =
  (typeof BILL_WORKFLOW_STATIC_CONDITION_FIELDS)[number];
// prettier-ignore
export type BillWorkflowConditionField =
  | BillWorkflowStaticConditionField
  | SegmentConditionFieldName;

export interface BillWorkflowConditionFieldDefinition {
  scope: BillWorkflowConditionScope;
  valueKind: BillWorkflowConditionValueKind;
  operators: readonly BillWorkflowConditionOperator[];
  values?: readonly string[];
  /**
   * Facts only (D39): the fact can also be tested in an approval-chain condition
   * (`bill_approval_workflow_init`), not only in `bill_updated`. Set per fact, never per trigger.
   */
  approvalChain?: boolean;
}

export const BILL_WORKFLOW_FIELD_DEFINITIONS = {
  vendor: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  amount: { scope: 'BILL', valueKind: 'number', operators: BILL_NUMBER_OPERATORS },
  bill_issue_date: { scope: 'BILL', valueKind: 'date', operators: BILL_DATE_OPERATORS },
  bill_type: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_TYPE_OPERATORS,
    values: Object.values(ApBillType),
  },
  payment_method: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_PAYMENT_METHOD_OPERATORS,
    values: Object.values(PaymentMethodType),
  },
  cost_center: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  department: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  gl_account: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  managerial_account: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  line_item_id: { scope: 'LINE_ITEM', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  line_description: { scope: 'LINE_ITEM', valueKind: 'text', operators: BILL_TEXT_OPERATORS },
  line_amount: { scope: 'LINE_ITEM', valueKind: 'number', operators: BILL_NUMBER_OPERATORS },
  cfop_code: { scope: 'LINE_ITEM', valueKind: 'text', operators: BILL_TEXT_OPERATORS },
} as const satisfies Record<BillWorkflowStaticConditionField, BillWorkflowConditionFieldDefinition>;

/**
 * The reconciliation state of a bill (the `reconciliation_state` fact, B5): NONE when nothing counts
 * as reconciled, FULL when every counted payment is (or one bank/card link covers the whole bill),
 * PARTIAL in between. One definition for the rule field's values and the fact that computes them.
 */
export const BillReconciliationState = {
  NONE: 'NONE',
  PARTIAL: 'PARTIAL',
  FULL: 'FULL',
} as const;
export type BillReconciliationState =
  (typeof BillReconciliationState)[keyof typeof BillReconciliationState];

/**
 * How many of a bill's counted payments carry a barcode (the `barcode_presence` fact, B6): ALL when
 * every one does, SOME when at least one does and one does not, NONE when none does (including a
 * bill with no counted payment). One definition for the rule field's values and the fact.
 */
export const BillBarcodePresence = {
  ALL: 'ALL',
  SOME: 'SOME',
  NONE: 'NONE',
} as const;
export type BillBarcodePresence = (typeof BillBarcodePresence)[keyof typeof BillBarcodePresence];

/**
 * Whether the vendor of a bill has a rule for the bill's service type (the `service_vendor_rule`
 * fact, D41): HAS_RULE when the vendor rule in effect at the bill's competence says something about
 * withholdings or credits, NO_RULE when it does not, NO_SERVICE_TYPE when the bill has no service
 * type. "No service type" is not "no rule": a condition on one never matches the other.
 */
export const BillServiceVendorRule = {
  HAS_RULE: 'has_rule',
  NO_RULE: 'no_rule',
  NO_SERVICE_TYPE: 'no_service_type',
} as const;
export type BillServiceVendorRule =
  (typeof BillServiceVendorRule)[keyof typeof BillServiceVendorRule];

/** Fact-field definitions, keyed by the field name an author picks and the loader publishes. */
export type BillWorkflowFactFieldDefinitions = Readonly<
  Record<string, BillWorkflowConditionFieldDefinition>
>;

/**
 * Facts: bill state an author can test in a `bill_updated` workflow that is not a stored column of
 * the bill (for example "has an open payment alert"). They are bill_updated only (B10), except the
 * facts flagged `approvalChain` (D39, ALC-4563), which `getWorkflowConditionFieldDefinitions` also
 * merges into the approval chain (`bill_approval_workflow_init`). The reimbursement policy and every
 * other trigger never see a fact.
 *
 * The authoring ticket (ALC-4623) built the mechanism and proved it with test-only definitions.
 * Each fact ticket adds its own entries here, and the invariants test over this object
 * (`findBillUpdatedFactFieldDefinitionProblems`) keeps them well-formed.
 *
 * Every function below takes the registry as a parameter defaulting to this object, so a test (or a
 * story) can inject definitions without touching production behaviour.
 */
export const BILL_UPDATED_FACT_FIELD_DEFINITIONS = {
  // ALC-4625 (B3, B4), ALC-4497 fix 5 (B5/B12 CHANGED): how many of the counted payments are
  // RECONCILED (ap_payment.status only): none, some, or all.
  reconciliation_state: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_TYPE_OPERATORS,
    values: Object.values(BillReconciliationState),
  },
  // ALC-4625 (B9): the types of the bill's OPEN alerts, as a list. An empty list is a real value.
  open_alert_types: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_LIST_OPERATORS,
    values: Object.values(ApBillAlertType),
  },
  // ALC-4626 (B6): do the counted payments carry a barcode? Never null: no payment is NONE.
  barcode_presence: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_TYPE_OPERATORS,
    values: Object.values(BillBarcodePresence),
  },
  // ALC-4626 (B6): every barcode passes its check digits. Null = no payment has a barcode.
  barcode_valid: {
    scope: 'BILL',
    valueKind: 'boolean',
    operators: BILL_NULLABLE_BOOLEAN_OPERATORS,
  },
  // ALC-4626 (B6): every barcode that encodes an amount equals its payment's scheduled amount.
  // Null = no barcode encodes an amount (none, invalid, or an arrecadacao by quantity).
  barcode_amount_matches: {
    scope: 'BILL',
    valueKind: 'boolean',
    operators: BILL_NULLABLE_BOOLEAN_OPERATORS,
  },
  // ALC-4497 fix 5 (B8 CHANGED, replaces payer_kind): the client pays the bill back. Never null.
  is_client_reimbursable: {
    scope: 'BILL',
    valueKind: 'boolean',
    operators: BILL_BOOLEAN_OPERATORS,
    // ALC-4565 (D35): the "aprovar" workflow of the approval-rule offer tests the same conditions as
    // the "enviar" workflow, and the offer's "Despesa reembolsável" row is one of them.
    approvalChain: true,
  },
  // ALC-4563 (D39 to D41): the wave 2 facts. They also work in approval-chain conditions, so the
  // "approve" workflow of D35 tests the same facts as the "send" workflow.
  // The bill's fiscal service type (its id). Null when the bill has none.
  fiscal_service_type: {
    scope: 'BILL',
    valueKind: 'service_type',
    operators: BILL_SERVICE_TYPE_OPERATORS,
    approvalChain: true,
  },
  // D40: per tax, whether it applies and at which rate (always an explicit rate, P42), as DECIDED,
  // never the withheld amount. A bill with no recorded calculation, or one a person overrode (P39),
  // has no profile.
  withholding_profile: {
    scope: 'BILL',
    valueKind: 'withholding_profile',
    operators: BILL_PROFILE_OPERATORS,
    approvalChain: true,
  },
  // PIS/COFINS credit: generates, credit type, revenue use. No amounts.
  credit_profile: {
    scope: 'BILL',
    valueKind: 'credit_profile',
    operators: BILL_PROFILE_OPERATORS,
    approvalChain: true,
  },
  // Every allocation row and line of the bill has a target inside the given set, per dimension (D20).
  // A bill with no target in a named dimension, or only some rows or lines with one, is not within (P40).
  allocations_within_targets: {
    scope: 'BILL',
    valueKind: 'allocation_targets',
    operators: BILL_ALLOCATION_TARGET_OPERATORS,
    approvalChain: true,
  },
  // D41: does the vendor have a rule for the bill's service type.
  service_vendor_rule: {
    scope: 'BILL',
    valueKind: 'enum',
    operators: BILL_TYPE_OPERATORS,
    values: Object.values(BillServiceVendorRule),
    approvalChain: true,
  },
} as const satisfies BillWorkflowFactFieldDefinitions;

/** The names registered in BILL_UPDATED_FACT_FIELD_DEFINITIONS. */
export type BillUpdatedFactConditionField = Extract<
  keyof typeof BILL_UPDATED_FACT_FIELD_DEFINITIONS,
  string
>;

/** True when `field` is registered as a fact. Own keys only: `constructor` is not a fact. */
export function isBillUpdatedFactField(
  field: string,
  facts: BillWorkflowFactFieldDefinitions = BILL_UPDATED_FACT_FIELD_DEFINITIONS,
): boolean {
  return Object.prototype.hasOwnProperty.call(facts, field);
}

export function getBillUpdatedFactFieldDefinition(
  field: string,
  facts: BillWorkflowFactFieldDefinitions = BILL_UPDATED_FACT_FIELD_DEFINITIONS,
): BillWorkflowConditionFieldDefinition | undefined {
  return isBillUpdatedFactField(field, facts) ? facts[field] : undefined;
}

/**
 * Whether a workflow of `triggerTypeCode` may test the fact `field` (D39). Every fact works in
 * `bill_updated`. The approval chain (`bill_approval_workflow_init`) accepts only the facts flagged
 * `approvalChain`. No other trigger accepts any fact (B10). A name that is not a fact is false.
 */
export function isBillFactFieldAllowedForTrigger(
  field: string,
  triggerTypeCode: WorkflowTriggerTypeCode,
  facts: BillWorkflowFactFieldDefinitions = BILL_UPDATED_FACT_FIELD_DEFINITIONS,
): boolean {
  const definition = getBillUpdatedFactFieldDefinition(field, facts);
  if (definition === undefined) return false;
  if (triggerTypeCode === WorkflowTriggerTypeCode.BILL_UPDATED) return true;
  return (
    triggerTypeCode === WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT &&
    definition.approvalChain === true
  );
}

/**
 * Regex pattern for dynamic segment condition fields.
 * - Ensures the field starts with `segment[`
 * - Captures the UUID inside the brackets
 * - UUID charset is limited to hex characters plus dashes
 *
 * Example matches:
 * - "segment[123e4567-e89b-12d3-a456-426614174000]"
 * - "segment[abc123]"
 */
export const SEGMENT_FIELD_PATTERN = /^segment\[([0-9a-f-]+)\]$/i;

/**
 * Check if a workflow condition field is a dynamic segment field.
 */
export function isSegmentConditionField(field: string): field is SegmentConditionFieldName {
  return SEGMENT_FIELD_PATTERN.test(field);
}

/**
 * Extract the segment ID from a dynamic segment condition field.
 * Returns the inner UUID when the field matches the `segment[uuid]` pattern,
 * or null otherwise.
 */
export function parseSegmentIdFromConditionField(field: string): string | null {
  const match = SEGMENT_FIELD_PATTERN.exec(field);
  return match ? match[1] : null;
}

export const ENTITY_BOUND_WORKFLOW_CONDITION_FIELD_TO_REFERENCE_TYPE = {
  cost_center: 'cost_center',
  department: 'department',
  gl_account: 'gl_account',
  managerial_account: 'managerial_account',
} as const;
export const WORKFLOW_ENTITY_BOUND_CONDITION_FIELDS = [
  'cost_center',
  'department',
  'gl_account',
  'managerial_account',
] as const satisfies readonly (keyof typeof ENTITY_BOUND_WORKFLOW_CONDITION_FIELD_TO_REFERENCE_TYPE)[];
export type EntityBoundWorkflowConditionField =
  keyof typeof ENTITY_BOUND_WORKFLOW_CONDITION_FIELD_TO_REFERENCE_TYPE;
export type EntityBoundWorkflowReferenceType =
  (typeof ENTITY_BOUND_WORKFLOW_CONDITION_FIELD_TO_REFERENCE_TYPE)[EntityBoundWorkflowConditionField];

export function isBillWorkflowStaticConditionField(
  field: string,
): field is BillWorkflowStaticConditionField {
  return (BILL_WORKFLOW_STATIC_CONDITION_FIELDS as readonly string[]).includes(field);
}

export function isBillWorkflowConditionField(field: string): field is BillWorkflowConditionField {
  return isBillWorkflowStaticConditionField(field) || isSegmentConditionField(field);
}

export function getBillWorkflowConditionOperators(
  field: BillWorkflowConditionField,
): readonly BillWorkflowConditionOperator[] {
  if (isSegmentConditionField(field)) {
    return BILL_ENTITY_OPERATORS;
  }
  return BILL_WORKFLOW_FIELD_DEFINITIONS[field].operators;
}

export type PurchaseRequestWorkflowConditionScope = 'PURCHASE_REQUEST';

export const PURCHASE_REQUEST_WORKFLOW_FIELDS = [
  'entity',
  'vendor',
  'amount',
  'cost_center',
  'department',
  'gl_account',
  'managerial_account',
  'purchase_type',
  'need_by_date',
] as const;

export type PurchaseRequestWorkflowStaticConditionField =
  (typeof PURCHASE_REQUEST_WORKFLOW_FIELDS)[number];

// prettier-ignore
export type PurchaseRequestWorkflowConditionField =
  | PurchaseRequestWorkflowStaticConditionField
  | SegmentConditionFieldName;

export interface PurchaseRequestWorkflowConditionFieldDefinition {
  scope: PurchaseRequestWorkflowConditionScope;
  valueKind: BillWorkflowConditionValueKind;
  operators: readonly BillWorkflowConditionOperator[];
}

export const PURCHASE_REQUEST_WORKFLOW_FIELD_DEFINITIONS = {
  entity: { scope: 'PURCHASE_REQUEST', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  vendor: { scope: 'PURCHASE_REQUEST', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  amount: { scope: 'PURCHASE_REQUEST', valueKind: 'number', operators: BILL_NUMBER_OPERATORS },
  cost_center: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'entity',
    operators: BILL_ENTITY_OPERATORS,
  },
  department: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'entity',
    operators: BILL_ENTITY_OPERATORS,
  },
  gl_account: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'entity',
    operators: BILL_ENTITY_OPERATORS,
  },
  managerial_account: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'entity',
    operators: BILL_ENTITY_OPERATORS,
  },
  purchase_type: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'text',
    operators: BILL_TEXT_OPERATORS,
  },
  need_by_date: {
    scope: 'PURCHASE_REQUEST',
    valueKind: 'date',
    operators: BILL_DATE_OPERATORS,
  },
} as const satisfies Record<
  PurchaseRequestWorkflowStaticConditionField,
  PurchaseRequestWorkflowConditionFieldDefinition
>;

export function isPurchaseRequestWorkflowStaticConditionField(
  field: string,
): field is PurchaseRequestWorkflowStaticConditionField {
  return (PURCHASE_REQUEST_WORKFLOW_FIELDS as readonly string[]).includes(field);
}

export function isPurchaseRequestWorkflowConditionField(
  field: string,
): field is PurchaseRequestWorkflowConditionField {
  return isPurchaseRequestWorkflowStaticConditionField(field) || isSegmentConditionField(field);
}

export function getPurchaseRequestWorkflowConditionOperators(
  field: PurchaseRequestWorkflowConditionField,
): readonly BillWorkflowConditionOperator[] {
  if (isSegmentConditionField(field)) {
    return BILL_ENTITY_OPERATORS;
  }
  return PURCHASE_REQUEST_WORKFLOW_FIELD_DEFINITIONS[field].operators;
}

/**
 * Enum for workflow trigger type codes.
 * These codes match the values in the workflowTriggerType seed data.
 */
export const WorkflowTriggerTypeCode = {
  BILL_INTAKE: 'bill_intake',
  BILL_UPDATED: 'bill_updated',
  BILL_APPROVED: 'bill_approved',
  TRANSACTION_ACCOUNTING_CODE_UPDATED: 'transaction_accounting_code_updated',
  BILL_APPROVAL_WORKFLOW_INIT: 'bill_approval_workflow_init',
  BILL_REIMBURSEMENT_POLICY: 'bill_reimbursement_policy',
  PURCHASE_REQUEST_POLICY: 'purchase_request_policy',
  CONTRACT_REVIEW_VERSION_UPLOADED: 'contract_review_version_uploaded',
  CONTRACT_REVIEW_AI_COMPLETED: 'contract_review_ai_completed',
  CONTRACT_REVIEW_FLAGS_DECIDED: 'contract_review_flags_decided',
} as const;
export type WorkflowTriggerTypeCode =
  (typeof WorkflowTriggerTypeCode)[keyof typeof WorkflowTriggerTypeCode];

// ALC-4621 (owner Q1): bill_approved removed — the owner named this list explicitly as one a
// caller can read triggers from (the MCP catalog's entityPolicy.entityBoundTriggers surfaces it).
export const BILL_ENTITY_BINDING_TRIGGER_CODES = [
  WorkflowTriggerTypeCode.BILL_INTAKE,
  WorkflowTriggerTypeCode.BILL_UPDATED,
  WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT,
  WorkflowTriggerTypeCode.BILL_REIMBURSEMENT_POLICY,
] as const satisfies readonly WorkflowTriggerTypeCode[];

export const WORKFLOW_TRIGGER_TYPE_CODE_VALUES = [
  WorkflowTriggerTypeCode.BILL_INTAKE,
  WorkflowTriggerTypeCode.BILL_UPDATED,
  WorkflowTriggerTypeCode.BILL_APPROVED,
  WorkflowTriggerTypeCode.TRANSACTION_ACCOUNTING_CODE_UPDATED,
  WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT,
  WorkflowTriggerTypeCode.BILL_REIMBURSEMENT_POLICY,
  WorkflowTriggerTypeCode.PURCHASE_REQUEST_POLICY,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_VERSION_UPLOADED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_AI_COMPLETED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_FLAGS_DECIDED,
] as const satisfies readonly WorkflowTriggerTypeCode[];

/**
 * ALC-4621 (owner Q1): triggers that are seeded in `WorkflowTriggerTypeCode` but have no runner —
 * nothing ever evaluates a workflow saved on them, so they must not be offered or allowed on a new
 * save. Kept only so a reader can still label an existing row saved on one of them before this
 * decision (none can be created or edited going forward).
 */
export const NON_RUNNABLE_WORKFLOW_TRIGGER_TYPE_CODES = [
  WorkflowTriggerTypeCode.BILL_APPROVED,
  WorkflowTriggerTypeCode.TRANSACTION_ACCOUNTING_CODE_UPDATED,
] as const satisfies readonly WorkflowTriggerTypeCode[];

/**
 * ALC-4621 (owner Q1): the trigger codes a caller may pick for a NEW workflow — every seeded
 * trigger that actually runs. Use this (not `WORKFLOW_TRIGGER_TYPE_CODE_VALUES`) anywhere a caller
 * selects a trigger to create or save a rule against (API create DTO Swagger docs, MCP create/list
 * schemas). `WORKFLOW_TRIGGER_TYPE_CODE_VALUES` stays the full set for reading/filtering existing
 * rows, since rows on the non-runnable triggers can still exist and must remain readable,
 * deactivatable and deletable.
 */
export const SELECTABLE_WORKFLOW_TRIGGER_TYPE_CODE_VALUES = [
  WorkflowTriggerTypeCode.BILL_INTAKE,
  WorkflowTriggerTypeCode.BILL_UPDATED,
  WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT,
  WorkflowTriggerTypeCode.BILL_REIMBURSEMENT_POLICY,
  WorkflowTriggerTypeCode.PURCHASE_REQUEST_POLICY,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_VERSION_UPLOADED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_AI_COMPLETED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_FLAGS_DECIDED,
] as const satisfies readonly WorkflowTriggerTypeCode[];

export function isNonRunnableWorkflowTriggerTypeCode(
  code: WorkflowTriggerTypeCode | null | undefined,
): boolean {
  return (
    code != null && (NON_RUNNABLE_WORKFLOW_TRIGGER_TYPE_CODES as readonly string[]).includes(code)
  );
}

/**
 * ALC-4621 (owner Q1): stable code for refusing a create/update that names a trigger with no
 * runner. pt-BR + en copy in {@link WORKFLOW_TRIGGER_NOT_RUNNABLE_COPY}.
 */
export const WorkflowTriggerErrorCode = {
  NOT_RUNNABLE: 'WORKFLOW_TRIGGER_NOT_RUNNABLE',
} as const;
export type WorkflowTriggerErrorCode =
  (typeof WorkflowTriggerErrorCode)[keyof typeof WorkflowTriggerErrorCode];

/** pt-BR + en copy for {@link WorkflowTriggerErrorCode.NOT_RUNNABLE} (ALC-4621 F10): the trigger
 * exists, it just has no runner yet — never "does not exist". */
export const WORKFLOW_TRIGGER_NOT_RUNNABLE_COPY = {
  en: 'This trigger does not run rules yet.',
  'pt-BR': 'Este gatilho ainda não executa regras.',
} as const;

/**
 * Enum for workflow action type codes.
 * These codes match the values in the workflowActionType seed data.
 *
 * **Usage:**
 * - Used in API payloads (`CreateWorkflowActionPayload.actionTypeCode`) instead of UUIDs
 * - The backend resolves these codes to UUIDs internally by querying the `workflowActionType` table
 * - Frontend components use `ClassificationActionType` string literals, and mappers handle conversion
 * - See `apps/web/src/lib/wf-mapper.ts` for conversion functions between frontend types and backend codes
 *
 * **Example Flow:**
 * Frontend: 'set_cost_center' → Backend: WorkflowActionTypeCode.SET_COST_CENTER → DB: UUID
 * Frontend: 'set_custom_segment' → Backend: WorkflowActionTypeCode.SET_CUSTOM_SEGMENT → DB: UUID
 */
export const WorkflowActionTypeCode = {
  SET_GL_ACCOUNT: 'set_gl_account',
  SET_COST_CENTER: 'set_cost_center',
  SET_CUSTOM_SEGMENT: 'set_custom_segment',
  SET_DEPARTMENT: 'set_department',
  SET_MANAGERIAL_ACCOUNT: 'set_managerial_account',
  SET_ORIGIN_BANK_ACCOUNT: 'set_origin_bank_account',
  SUBMIT_FOR_APPROVAL: 'submit_for_approval',
  REQUEST_APPROVAL: 'request_approval',
  SEND_NOTIFICATION: 'send_notification',
  AUTO_APPROVE: 'auto_approve',
  REQUIRE_PURCHASE_ORDER: 'require_purchase_order',
  CONTRACT_REVIEW_ENQUEUE_INTAKE: 'contract_review_enqueue_intake',
} as const;
export type WorkflowActionTypeCode =
  (typeof WorkflowActionTypeCode)[keyof typeof WorkflowActionTypeCode];

export const WORKFLOW_ENTITY_BOUND_ACTION_TYPES = [
  WorkflowActionTypeCode.SET_GL_ACCOUNT,
  WorkflowActionTypeCode.SET_COST_CENTER,
  WorkflowActionTypeCode.SET_CUSTOM_SEGMENT,
  WorkflowActionTypeCode.SET_DEPARTMENT,
  WorkflowActionTypeCode.SET_MANAGERIAL_ACCOUNT,
  WorkflowActionTypeCode.SET_ORIGIN_BANK_ACCOUNT,
] as const satisfies readonly WorkflowActionTypeCode[];

export const WORKFLOW_ACTION_TYPE_CODE_VALUES = [
  WorkflowActionTypeCode.SET_GL_ACCOUNT,
  WorkflowActionTypeCode.SET_COST_CENTER,
  WorkflowActionTypeCode.SET_CUSTOM_SEGMENT,
  WorkflowActionTypeCode.SET_DEPARTMENT,
  WorkflowActionTypeCode.SET_MANAGERIAL_ACCOUNT,
  WorkflowActionTypeCode.SET_ORIGIN_BANK_ACCOUNT,
  WorkflowActionTypeCode.SUBMIT_FOR_APPROVAL,
  WorkflowActionTypeCode.REQUEST_APPROVAL,
  WorkflowActionTypeCode.SEND_NOTIFICATION,
  WorkflowActionTypeCode.AUTO_APPROVE,
  WorkflowActionTypeCode.REQUIRE_PURCHASE_ORDER,
  WorkflowActionTypeCode.CONTRACT_REVIEW_ENQUEUE_INTAKE,
] as const satisfies readonly WorkflowActionTypeCode[];

export const ENTITY_BOUND_WORKFLOW_ACTION_TYPE_CODES = [
  WorkflowActionTypeCode.SET_GL_ACCOUNT,
  WorkflowActionTypeCode.SET_COST_CENTER,
  WorkflowActionTypeCode.SET_CUSTOM_SEGMENT,
  WorkflowActionTypeCode.SET_DEPARTMENT,
  WorkflowActionTypeCode.SET_MANAGERIAL_ACCOUNT,
  WorkflowActionTypeCode.SET_ORIGIN_BANK_ACCOUNT,
] as const satisfies readonly WorkflowActionTypeCode[];

export const WORKFLOW_ENTITY_ID_NOT_APPLICABLE_TRIGGER_CODES = [
  WorkflowTriggerTypeCode.PURCHASE_REQUEST_POLICY,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_VERSION_UPLOADED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_AI_COMPLETED,
  WorkflowTriggerTypeCode.CONTRACT_REVIEW_FLAGS_DECIDED,
] as const satisfies readonly WorkflowTriggerTypeCode[];

const BILL_ACTION_TYPE_CODE_VALUES = WORKFLOW_ACTION_TYPE_CODE_VALUES.filter(
  (code) =>
    code !== WorkflowActionTypeCode.CONTRACT_REVIEW_ENQUEUE_INTAKE &&
    code !== WorkflowActionTypeCode.SUBMIT_FOR_APPROVAL,
);

const BILL_UPDATED_ACTION_TYPE_CODE_VALUES = [
  ...BILL_ACTION_TYPE_CODE_VALUES,
  WorkflowActionTypeCode.SUBMIT_FOR_APPROVAL,
] as const satisfies readonly WorkflowActionTypeCode[];

const PURCHASE_REQUEST_ACTION_TYPE_CODE_VALUES = [
  WorkflowActionTypeCode.REQUEST_APPROVAL,
  WorkflowActionTypeCode.SEND_NOTIFICATION,
  WorkflowActionTypeCode.AUTO_APPROVE,
] as const satisfies readonly WorkflowActionTypeCode[];

const CONTRACT_REVIEW_ACTION_TYPE_CODE_VALUES = [
  WorkflowActionTypeCode.CONTRACT_REVIEW_ENQUEUE_INTAKE,
] as const satisfies readonly WorkflowActionTypeCode[];

/** Allowed action codes by trigger, shared by workflow authoring clients and catalogs. */
export const WORKFLOW_ACTION_TYPE_CODES_BY_TRIGGER = {
  [WorkflowTriggerTypeCode.BILL_INTAKE]: BILL_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.BILL_UPDATED]: BILL_UPDATED_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.BILL_APPROVED]: BILL_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.TRANSACTION_ACCOUNTING_CODE_UPDATED]: BILL_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT]: BILL_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.BILL_REIMBURSEMENT_POLICY]: BILL_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.PURCHASE_REQUEST_POLICY]: PURCHASE_REQUEST_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_VERSION_UPLOADED]:
    CONTRACT_REVIEW_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_AI_COMPLETED]: CONTRACT_REVIEW_ACTION_TYPE_CODE_VALUES,
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_FLAGS_DECIDED]: CONTRACT_REVIEW_ACTION_TYPE_CODE_VALUES,
} as const satisfies Record<WorkflowTriggerTypeCode, readonly WorkflowActionTypeCode[]>;

/** Runtime schemas for every workflow action configuration. */
export const WORKFLOW_ACTION_CONFIG_SCHEMAS = {
  [WorkflowActionTypeCode.SET_GL_ACCOUNT]: SetGlAccountActionConfigSchema,
  [WorkflowActionTypeCode.SET_COST_CENTER]: SetCostCenterActionConfigSchema,
  [WorkflowActionTypeCode.SET_CUSTOM_SEGMENT]: SetCustomSegmentActionConfigSchema,
  [WorkflowActionTypeCode.SET_DEPARTMENT]: SetDepartmentActionConfigSchema,
  [WorkflowActionTypeCode.SET_MANAGERIAL_ACCOUNT]: SetManagerialAccountActionConfigSchema,
  [WorkflowActionTypeCode.SET_ORIGIN_BANK_ACCOUNT]: SetOriginBankAccountActionConfigSchema,
  [WorkflowActionTypeCode.SUBMIT_FOR_APPROVAL]: SubmitForApprovalActionConfigSchema,
  [WorkflowActionTypeCode.REQUEST_APPROVAL]: RequestApprovalActionConfigSchema,
  [WorkflowActionTypeCode.SEND_NOTIFICATION]: SendNotificationActionConfigSchema,
  [WorkflowActionTypeCode.AUTO_APPROVE]: AutoApproveActionConfigSchema,
  [WorkflowActionTypeCode.REQUIRE_PURCHASE_ORDER]: RequirePurchaseOrderActionConfigSchema,
  [WorkflowActionTypeCode.CONTRACT_REVIEW_ENQUEUE_INTAKE]:
    ContractReviewEnqueueIntakeActionConfigSchema,
} as const satisfies Record<WorkflowActionTypeCode, object>;

const BILL_APPROVAL_WORKFLOW_FIELD_DEFINITIONS = Object.fromEntries(
  Object.entries(BILL_WORKFLOW_FIELD_DEFINITIONS).filter(
    ([, definition]) => definition.scope === 'BILL',
  ),
) as Partial<typeof BILL_WORKFLOW_FIELD_DEFINITIONS>;

const REIMBURSEMENT_POLICY_FIELD_DEFINITIONS = {
  ...BILL_WORKFLOW_FIELD_DEFINITIONS,
  category: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
  expense_category: { scope: 'BILL', valueKind: 'entity', operators: BILL_ENTITY_OPERATORS },
} as const;

/** Allowed static condition definitions by workflow trigger. Dynamic segments use SEGMENT_FIELD_PATTERN. */
export const WORKFLOW_CONDITION_FIELD_DEFINITIONS_BY_TRIGGER = {
  [WorkflowTriggerTypeCode.BILL_INTAKE]: BILL_WORKFLOW_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.BILL_UPDATED]: {
    ...BILL_WORKFLOW_FIELD_DEFINITIONS,
    ...BILL_UPDATED_FACT_FIELD_DEFINITIONS,
  },
  [WorkflowTriggerTypeCode.BILL_APPROVED]: BILL_WORKFLOW_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.TRANSACTION_ACCOUNTING_CODE_UPDATED]: BILL_WORKFLOW_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT]: BILL_APPROVAL_WORKFLOW_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.BILL_REIMBURSEMENT_POLICY]: REIMBURSEMENT_POLICY_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.PURCHASE_REQUEST_POLICY]: PURCHASE_REQUEST_WORKFLOW_FIELD_DEFINITIONS,
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_VERSION_UPLOADED]: {},
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_AI_COMPLETED]: {},
  [WorkflowTriggerTypeCode.CONTRACT_REVIEW_FLAGS_DECIDED]: {},
} as const satisfies Record<WorkflowTriggerTypeCode, Readonly<Record<string, object>>>;

/**
 * Field definitions a trigger accepts. For `bill_updated` the fact registry is merged in at call
 * time (the registry can be injected); a static bill field always wins over a fact of the same name.
 * The approval chain (`bill_approval_workflow_init`) gets its static set plus the facts flagged
 * `approvalChain` (D39), and its static object itself when no fact is flagged. Every other trigger
 * gets its fixed set back, never the facts.
 */
export function getWorkflowConditionFieldDefinitions(
  triggerTypeCode: WorkflowTriggerTypeCode,
  facts: BillWorkflowFactFieldDefinitions = BILL_UPDATED_FACT_FIELD_DEFINITIONS,
): Readonly<Record<string, object>> {
  if (triggerTypeCode === WorkflowTriggerTypeCode.BILL_APPROVAL_WORKFLOW_INIT) {
    const fixed = WORKFLOW_CONDITION_FIELD_DEFINITIONS_BY_TRIGGER[triggerTypeCode];
    const merged: Record<string, object> = { ...fixed };
    let added = false;
    for (const [field, definition] of Object.entries(facts)) {
      if (
        definition.approvalChain === true &&
        !Object.prototype.hasOwnProperty.call(merged, field)
      ) {
        merged[field] = definition;
        added = true;
      }
    }
    return added ? merged : fixed;
  }
  if (triggerTypeCode !== WorkflowTriggerTypeCode.BILL_UPDATED) {
    return WORKFLOW_CONDITION_FIELD_DEFINITIONS_BY_TRIGGER[triggerTypeCode];
  }
  const merged: Record<string, object> = { ...BILL_WORKFLOW_FIELD_DEFINITIONS };
  for (const [field, definition] of Object.entries(facts)) {
    if (!Object.prototype.hasOwnProperty.call(merged, field)) {
      merged[field] = definition;
    }
  }
  return merged;
}

const FACT_SUPPORTED_VALUE_KINDS: readonly BillWorkflowConditionValueKind[] = [
  'boolean',
  'enum',
  'number',
  'text',
  'date',
  'service_type',
  'withholding_profile',
  'credit_profile',
  'allocation_targets',
];

const PROFILE_VALUE_KINDS: readonly BillWorkflowConditionValueKind[] = [
  'withholding_profile',
  'credit_profile',
];

/**
 * Problems in a fact registry, as human-readable lines (empty = well-formed). Guards the contract
 * the fact tickets rely on: a fact cannot shadow another trigger's field, an enum fact carries its
 * values, the operators fit the value kind, and the kind is one the authoring screens can render.
 */
export function findBillUpdatedFactFieldDefinitionProblems(
  facts: BillWorkflowFactFieldDefinitions = BILL_UPDATED_FACT_FIELD_DEFINITIONS,
): string[] {
  const reservedNames = new Set<string>(Object.keys(BILL_WORKFLOW_FIELD_DEFINITIONS));
  for (const triggerTypeCode of WORKFLOW_TRIGGER_TYPE_CODE_VALUES) {
    if (triggerTypeCode !== WorkflowTriggerTypeCode.BILL_UPDATED) {
      for (const name of Object.keys(
        WORKFLOW_CONDITION_FIELD_DEFINITIONS_BY_TRIGGER[triggerTypeCode],
      )) {
        reservedNames.add(name);
      }
    }
  }
  const knownOperators = new Set<string>(BILL_WORKFLOW_CONDITION_OPERATOR_VALUES);
  const booleanOperators = new Set<string>(BILL_NULLABLE_BOOLEAN_OPERATORS);
  const listOperators = new Set<string>(BILL_LIST_OPERATORS);

  const problems: string[] = [];
  for (const [name, definition] of Object.entries(facts)) {
    if (reservedNames.has(name) || isSegmentConditionField(name)) {
      problems.push(`Fact "${name}" reuses the name of another workflow condition field.`);
    }
    if (!FACT_SUPPORTED_VALUE_KINDS.includes(definition.valueKind)) {
      problems.push(
        `Fact "${name}" has value kind "${definition.valueKind}", which authoring cannot render.`,
      );
    }
    if (definition.operators.length === 0) {
      problems.push(`Fact "${name}" offers no operators.`);
    }
    for (const operator of definition.operators) {
      if (!knownOperators.has(operator)) {
        problems.push(`Fact "${name}" offers the unknown operator "${operator}".`);
      } else if (definition.valueKind === 'boolean' && !booleanOperators.has(operator)) {
        problems.push(`Fact "${name}" is boolean but offers "${operator}".`);
      } else if (listOperators.has(operator) && definition.valueKind !== 'enum') {
        problems.push(
          `Fact "${name}" offers the list operator "${operator}" without being an enum.`,
        );
      } else if (
        operator === BillWorkflowConditionOperator.same_profile_as &&
        !PROFILE_VALUE_KINDS.includes(definition.valueKind)
      ) {
        problems.push(`Fact "${name}" offers "${operator}" without being a profile.`);
      } else if (
        isBillAllocationTargetOperator(operator) &&
        definition.valueKind !== 'allocation_targets'
      ) {
        problems.push(`Fact "${name}" offers "${operator}" without being allocation targets.`);
      } else if (
        PROFILE_VALUE_KINDS.includes(definition.valueKind) &&
        operator !== BillWorkflowConditionOperator.same_profile_as
      ) {
        problems.push(`Fact "${name}" is a profile but offers "${operator}".`);
      } else if (
        definition.valueKind === 'allocation_targets' &&
        !isBillAllocationTargetOperator(operator)
      ) {
        problems.push(`Fact "${name}" is allocation targets but offers "${operator}".`);
      }
    }
    if (definition.valueKind === 'enum' && (definition.values ?? []).length === 0) {
      problems.push(`Fact "${name}" is an enum without values.`);
    }
  }
  return problems;
}

/**
 * Stable codes for the refusals that concern fact fields (the field is not allowed on the trigger, the
 * operator or the value does not fit the fact). pt-BR and en copy: apps/web validation-error-messages.
 */
export const WorkflowConditionFactErrorCode = {
  FIELD_NOT_ALLOWED_FOR_TRIGGER: 'WORKFLOW_CONDITION_FACT_FIELD_NOT_ALLOWED_FOR_TRIGGER',
  OPERATOR_NOT_ALLOWED: 'WORKFLOW_CONDITION_FACT_OPERATOR_NOT_ALLOWED',
  VALUE_INVALID: 'WORKFLOW_CONDITION_FACT_VALUE_INVALID',
  /** A withholding profile names a tax with no rate (P42): the profile always holds explicit rates. */
  PROFILE_RATE_REQUIRED: 'WORKFLOW_CONDITION_FACT_PROFILE_RATE_REQUIRED',
  /** ALC-4658 (P50): the percents of an `exact_split` dimension do not sum to exactly 100,00. */
  SPLIT_SUM_NOT_100: 'WORKFLOW_CONDITION_FACT_SPLIT_SUM_NOT_100',
} as const;
export type WorkflowConditionFactErrorCode =
  (typeof WorkflowConditionFactErrorCode)[keyof typeof WorkflowConditionFactErrorCode];

export const WorkflowEntityErrorCode = {
  MISMATCH: 'WORKFLOW_ENTITY_MISMATCH',
  REQUIRED: 'WORKFLOW_ENTITY_REQUIRED',
  NOT_FOUND: 'WORKFLOW_ENTITY_NOT_FOUND',
  NOT_APPLICABLE: 'WORKFLOW_ENTITY_NOT_APPLICABLE',
  UNRESOLVED_REFERENCE: 'WORKFLOW_ENTITY_UNRESOLVED_REFERENCE',
  BANK_ACCOUNT_MISMATCH: 'WORKFLOW_BANK_ACCOUNT_ENTITY_MISMATCH',
} as const;
export type WorkflowEntityErrorCode =
  (typeof WorkflowEntityErrorCode)[keyof typeof WorkflowEntityErrorCode];

/**
 * ALC-4627: stable codes for a refused line quantifier. Clients map the code, never the message.
 * The pt-BR and en copy lives in `apps/web/src/lib/validation-error-messages.ts`.
 */
export const WorkflowConditionErrorCode = {
  /** A quantifier on a condition that is not a LINE_ITEM leaf (a bill field, or a group node). */
  LINE_QUANTIFIER_NOT_LINE_FIELD: 'WORKFLOW_CONDITION_LINE_QUANTIFIER_NOT_LINE_FIELD',
  /** A quantifier on a rule whose trigger does not evaluate line conditions over the bill's lines. */
  LINE_QUANTIFIER_TRIGGER_UNSUPPORTED: 'WORKFLOW_CONDITION_LINE_QUANTIFIER_TRIGGER_UNSUPPORTED',
  /** ALC-4621 (R18): an operator the evaluator does not know. */
  OPERATOR_INVALID: 'WORKFLOW_CONDITION_OPERATOR_INVALID',
  /** ALC-4621 (R18): a bill field the evaluator cannot read for this trigger. */
  FIELD_UNSUPPORTED: 'WORKFLOW_CONDITION_FIELD_UNSUPPORTED',
  /** ALC-4621 (R18): a known bill field with an operator it does not support. */
  OPERATOR_UNSUPPORTED_FOR_FIELD: 'WORKFLOW_CONDITION_OPERATOR_UNSUPPORTED_FOR_FIELD',
} as const;
export type WorkflowConditionErrorCode =
  (typeof WorkflowConditionErrorCode)[keyof typeof WorkflowConditionErrorCode];

/**
 * ALC-4644 (D33): the stable event codes of the workflow audit trail. Every create, edit, status
 * change and delete of a workflow writes one `AuditLog` row with one of these. Readers match the
 * code, never the action text.
 *
 * A call that changes only the status writes WORKFLOW_STATUS_CHANGED. A call that changes the
 * status together with anything else writes one WORKFLOW_UPDATED row (its `changedFields` names
 * the status too).
 */
export const WorkflowAuditEventType = {
  CREATED: 'WORKFLOW_CREATED',
  UPDATED: 'WORKFLOW_UPDATED',
  STATUS_CHANGED: 'WORKFLOW_STATUS_CHANGED',
  DELETED: 'WORKFLOW_DELETED',
} as const;
export type WorkflowAuditEventType =
  (typeof WorkflowAuditEventType)[keyof typeof WorkflowAuditEventType];

/** `AuditLog.eventCategory` and `AuditLog.resourceType` of every workflow audit row. */
export const WORKFLOW_AUDIT_EVENT_CATEGORY = 'workflow';
export const WORKFLOW_AUDIT_RESOURCE_TYPE = 'WORKFLOW';

/** One condition of a workflow, as the audit trail records it. No ids: ids change on every save. */
export interface WorkflowAuditConditionSnapshot {
  /** Position of the parent group in `conditions`; null at the top level. */
  parent: number | null;
  /** How this row joins its siblings: AND or OR. */
  join: string;
  field: string | null;
  operator: string | null;
  value: unknown;
  /** ANY | ALL over a bill's lines; null reads as ALL. */
  quantifier: 'ANY' | 'ALL' | null;
  order: number;
}

/** One action of a workflow, as the audit trail records it. */
export interface WorkflowAuditActionSnapshot {
  type: string | null;
  order: number;
  config: unknown;
}

/**
 * The stable picture of a workflow that `AuditLog.details.before` / `.after` hold.
 *
 * Left out on purpose: ids (a save recreates condition and action rows), timestamps, and the
 * canvas layout (`metadata` node `position` and `canvasSize`), which is noise. The rest of
 * `metadata` stays, because canvas wiring may live only there.
 */
export interface WorkflowAuditSnapshot {
  name: string;
  description: string | null;
  status: RowStatus;
  entityId: string | null;
  /** Trigger type code, for example `bill_updated`. */
  triggerType: string | null;
  metadata: unknown;
  /**
   * ALC-4564: where the rule came from, or null for a hand-made or MCP-made rule. Set once at
   * create and immutable, so it never appears in `changedFields`; it is here so the create row
   * says how the rule was born. Snapshots of version 1 have no `origin` key: read it as null.
   */
  origin: WorkflowAuditOriginSnapshot | null;
  conditions: WorkflowAuditConditionSnapshot[];
  actions: WorkflowAuditActionSnapshot[];
}

/** The origin of a rule as the audit trail records it. */
export interface WorkflowAuditOriginSnapshot {
  kind: string;
  text: string | null;
  sourceBillId: string | null;
}

/** The top-level keys of {@link WorkflowAuditSnapshot}, in the order `changedFields` lists them. */
export const WORKFLOW_AUDIT_SNAPSHOT_FIELDS = [
  'name',
  'description',
  'status',
  'entityId',
  'triggerType',
  'metadata',
  'origin',
  'conditions',
  'actions',
] as const satisfies readonly (keyof WorkflowAuditSnapshot)[];
export type WorkflowAuditSnapshotField = (typeof WORKFLOW_AUDIT_SNAPSHOT_FIELDS)[number];

/**
 * The shape version of {@link WorkflowAuditDetails}. It is stored in every audit row, so a reader
 * of an old row knows which snapshot fields to expect. Bump it when the snapshot or the details
 * change shape; never rewrite old rows.
 *
 * Version 2 (ALC-4564) adds `origin` to the snapshot. A version 1 row has no `origin` key.
 */
export const WORKFLOW_AUDIT_SNAPSHOT_VERSION = 2;

/**
 * Who made a workflow change, and through which door (ALC-4644, D37). `userId` is the person the
 * request authenticated as. For `tokenSource` `'session'` that person is at the keyboard. For
 * `'mcp'` it is the person who delegated access to an agent: an agent edit is NOT the person's own
 * edit, so the authority check (D37) must not read it as theirs. `'unknown'` is a bearer of
 * unrecognised origin.
 */
export interface WorkflowAuditActor {
  userId: string | null;
  tokenSource: AuthTokenSource;
  /** The delegated agent, when the request carries one. The API does not receive it today. */
  agentId?: string;
}

/** `AuditLog.details` of a workflow audit row. create: before is null. delete: after is null. */
export interface WorkflowAuditDetails {
  snapshotVersion: typeof WORKFLOW_AUDIT_SNAPSHOT_VERSION;
  actor: WorkflowAuditActor;
  before: WorkflowAuditSnapshot | null;
  after: WorkflowAuditSnapshot | null;
  /** The snapshot fields that differ. Empty on create and delete. */
  changedFields: WorkflowAuditSnapshotField[];
}

export interface WorkflowTriggerTypeResponse {
  triggerTypeId: string;
  code: string;
  name: string;
  description: string | null;
  status: RowStatus;
  createdAt: string;
  updatedAt: string | null;
}

export interface WorkflowResponse {
  workflowId: string;
  tenantId: string;
  entityId: string | null;
  triggerTypeId: string;
  name: string;
  description: string | null;
  status: RowStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  /**
   * ALC-4644: the last person whose own save changed the rule. A save that changes nothing does
   * not move it. Null when the last change came through an MCP or other API token (D37: a token
   * has no authority); the audit row of that change still names the delegating user.
   */
  updatedBy: string | null;
  /**
   * ALC-4564 (D34): where the rule came from (a description the person wrote, or an approval
   * offer), or null for a hand-made or MCP-made rule. Set once at create; `createdBy` and
   * `createdAt` are the rule's own. Absent on responses built before the field existed.
   */
  origin?: WorkflowOriginResponse | null;
  /**
   * Optional metadata for storing canvas layout information (node positions, connections).
   * Used by approval workflow canvas to persist visual layout.
   */
  metadata?: Record<string, unknown>;
  /**
   * Optional nested trigger type relation.
   * Included when fetching a single workflow via GET /wf/workflows/:id
   */
  triggerType?: WorkflowTriggerTypeResponse;
  /**
   * Optional nested conditions relation.
   * Included when fetching a single workflow via GET /wf/workflows/:id
   */
  conditions?: WorkflowConditionResponse[];
  /**
   * Optional nested actions relation.
   * Included when fetching a single workflow via GET /wf/workflows/:id
   */
  actions?: WorkflowActionResponse[];
}

/**
 * Response type for workflow condition data.
 *
 * Note: The backend may include additional read-only properties not reflected in this DTO,
 * such as nested relations (e.g., `entityRef`, `parentCondition`) when fetching workflows
 * via `WorkflowService.findOne`. Consumers should treat any extra fields as internal
 * implementation details and should not rely on them being present.
 */
export interface WorkflowConditionResponse {
  workflowConditionId: string;
  workflowId: string;
  parentConditionId: string | null;
  conditionGroup: 'AND' | 'OR' | null;
  field: string | null;
  operator: string | null;
  value: unknown;
  entityRefId: string | null;
  conditionOrder: number;
  /**
   * ALC-4627: how a `bill_updated` rule reads this LINE_ITEM condition across the lines of a bill.
   * ANY = some line satisfies it (together with the other line conditions of its group); ALL =
   * every line does. `null` on a line condition reads as ALL; it is always `null` on any other
   * condition (the API refuses a value there).
   */
  lineQuantifier: WorkflowConditionLineQuantifier | null;
}

/**
 * Response type for workflow action data.
 *
 * Note: The backend may include additional read-only properties not reflected in this DTO,
 * such as nested relations (e.g., `actionType`) when fetching workflows via
 * `WorkflowService.findOne`. Consumers should treat any extra fields as internal
 * implementation details and should not rely on them being present.
 */
export interface WorkflowActionResponse {
  workflowActionId: string;
  workflowId: string;
  actionTypeId: string;
  /**
   * Optional action type code (populated when actions are fetched with relations).
   * Kept in sync with workflow.service mapping so consumers don't need extra lookups.
   */
  actionTypeCode?: WorkflowActionTypeCode;
  actionSequence: number;
  actionConfig: Record<string, unknown>;
}

export interface CreateWorkflowConditionPayload {
  parentConditionId?: string;
  conditionGroup?: 'AND' | 'OR';
  field?: string;
  operator?: string;
  value?: unknown;
  entityRefId?: string;
  conditionOrder?: number;
  /** ALC-4627: only on a LINE_ITEM field of a `bill_updated` rule. Omitted or `null` = ALL. */
  lineQuantifier?: WorkflowConditionLineQuantifier | null;
}

export interface CreateWorkflowActionPayload {
  /**
   * Action type code enum (e.g., WorkflowActionTypeCode.SET_COST_CENTER).
   * Uses enum codes instead of UUIDs for better maintainability.
   * The backend resolves these codes to UUIDs internally by querying the workflowActionType table.
   * See `apps/web/src/lib/wf-mapper.ts` for mapper functions that handle conversion between
   * frontend ClassificationActionType string literals and backend enum codes.
   */
  actionTypeCode: WorkflowActionTypeCode;
  actionSequence: number;
  actionConfig: Record<string, unknown>;
}

/**
 * Payload for creating a workflow.
 *
 * This payload supports bulk creation of workflows with all their parts in a single request.
 * When `conditions` and/or `actions` arrays are provided, they will be created atomically
 * with the workflow in a single transaction.
 *
 * For conditions with parent-child relationships, use array indices as `parentConditionId`
 * values (e.g., "0", "1", "2"). The backend will resolve these to actual database IDs.
 */
export interface CreateWorkflowPayload {
  /**
   * When true, and the saved rule is an active bill-updated rule, check the draft and submitted
   * bills already waiting that the rule's conditions match. Not stored. A later save asks again.
   */
  checkWaitingBills?: boolean;
  triggerTypeCode: WorkflowTriggerTypeCode;
  entityId?: string | null;
  name: string;
  description?: string | null;
  status?: RowStatus;
  /**
   * ALC-4564: where the rule came from. Only a person at the screen declares it (an MCP or other
   * API token is refused), only on a bill rule, and it cannot change afterwards. Provenance, not
   * authority. See `checkWorkflowOriginInput`.
   */
  origin?: WorkflowOriginInput;
  /**
   * Optional metadata for storing canvas layout information (node positions, connections).
   * Used by approval workflow canvas to persist visual layout.
   */
  metadata?: Record<string, unknown>;
  /**
   * Optional array of conditions to create with the workflow.
   * Parent-child relationships can be specified using array indices as parentConditionId.
   */
  conditions?: CreateWorkflowConditionPayload[];
  /**
   * Optional array of actions to create with the workflow.
   */
  actions?: CreateWorkflowActionPayload[];
}

/**
 * Payload for updating a workflow.
 *
 * Allows updating name, description, status, conditions, and actions.
 * When conditions and/or actions arrays are provided, they will be updated atomically
 * with the workflow in a single transaction.
 *
 * For conditions with parent-child relationships, use array indices as `parentConditionId`
 * values (e.g., "0", "1", "2"). The backend will resolve these to actual database IDs.
 */
export interface UpdateWorkflowPayload {
  /** See {@link CreateWorkflowPayload.checkWaitingBills}. Not stored. */
  checkWaitingBills?: boolean;
  entityId?: string | null;
  name?: string;
  description?: string | null;
  status?: RowStatus;
  /**
   * Optional metadata for storing canvas layout information (node positions, connections).
   * Used by approval workflow canvas to persist visual layout.
   */
  metadata?: Record<string, unknown>;
  /**
   * Optional array of conditions to update with the workflow.
   * Parent-child relationships can be specified using array indices as parentConditionId.
   */
  conditions?: CreateWorkflowConditionPayload[];
  /**
   * Optional array of actions to update with the workflow.
   */
  actions?: CreateWorkflowActionPayload[];
}

/**
 * Action config types and schemas for workflow actions (classification + approval).
 *
 * The types are automatically inferred from Zod schemas defined in wf-schemas.ts.
 * This ensures type safety and runtime validation are always in sync for both
 * classification actions (set cost center, set GL, etc.) and approval actions
 * (request approval, send notification, auto-approve).
 *
 * For validation schemas, import from '@alinea/types/wf-schemas'.
 * For TypeScript types, use the exports below.
 */
export type {
  ActionConfigItem,
  ApproverConfig,
  AutoApproveActionConfig,
  ContractReviewEnqueueIntakeActionConfig,
  RequestApprovalActionConfig,
  RequirePurchaseOrderActionConfig,
  SendNotificationActionConfig,
  SetCostCenterActionConfig,
  SetCustomSegmentActionConfig,
  SetDepartmentActionConfig,
  SetGlAccountActionConfig,
  SetManagerialAccountActionConfig,
  SetOriginBankAccountActionConfig,
  StandardizedActionConfig,
} from './wf-schemas.js';

// Re-export schemas for convenience
export {
  ActionConfigItemSchema,
  AutoApproveActionConfigSchema,
  ContractReviewEnqueueIntakeActionConfigSchema,
  migrateLegacyApprovalConfig,
  RequestApprovalActionConfigSchema,
  RequirePurchaseOrderActionConfigSchema,
  SendNotificationActionConfigSchema,
  SetCostCenterActionConfigSchema,
  SetCustomSegmentActionConfigSchema,
  SetDepartmentActionConfigSchema,
  SetGlAccountActionConfigSchema,
  SetManagerialAccountActionConfigSchema,
  SetOriginBankAccountActionConfigSchema,
  StandardizedActionConfigSchema,
} from './wf-schemas.js';

/**
 * ALC-4621 D43: stable error code raised when a workflow run row could not be written. The write it
 * describes is rolled back with it (fail closed); callers match on this code, never on the message.
 */
export const WORKFLOW_RUN_LOG_WRITE_FAILED = 'WORKFLOW_RUN_LOG_WRITE_FAILED' as const;

/** pt-BR + en copy for {@link WORKFLOW_RUN_LOG_WRITE_FAILED}. */
export const WORKFLOW_RUN_LOG_WRITE_FAILED_COPY = {
  en: "The automation activity log could not be written, so the automation's change was not saved. Please try again.",
  'pt-BR':
    'Não foi possível registrar a atividade da automação, por isso a alteração da automação não foi salva. Tente novamente.',
} as const;

/**
 * ALC-4621 D43: stable reason codes written to `wf_workflow_execution(_action).reason_code`.
 * One const so every runner names the same code for the same reason; pt-BR + en copy below.
 * Not all are wired yet — a runner that does not yet write its row still reserves its code here
 * so the read API and later slices share one vocabulary.
 */
export const WorkflowRunReasonCode = {
  /** Fill pass / chain: the bill's classification is marked protected from automation. */
  CLASSIFICATION_PROTECTED: 'CLASSIFICATION_PROTECTED',
  /** Chain: the bill's budget check returned REQUIRE_EXCEPTION, so the rule's auto_approve action
   * did not run (the bill needs a budget exception approved by a person first). The budget result
   * (action + budget line ids, no amounts) is in the execution's `details.budget`. */
  AUTO_APPROVE_SUPPRESSED: 'AUTO_APPROVE_SUPPRESSED',
  /** Worker submit: a dependent cascade has not settled yet. */
  CASCADE_NOT_SETTLED: 'CASCADE_NOT_SETTLED',
  /** Worker submit: a payment/settlement gap blocks the transition. */
  PAYMENT_GAP: 'PAYMENT_GAP',
  /** Worker submit: the bill status transition itself failed. */
  STATUS_TRANSITION_FAILED: 'STATUS_TRANSITION_FAILED',
  /** Worker submit: the budget check hard-blocked the bill (over budget, no exception allowed), so
   * the submit rejected it instead of sending it for approval. */
  BUDGET_HARD_BLOCK: 'BUDGET_HARD_BLOCK',
  /** Worker submit: the bill has an open vendor rule divergence nobody has resolved (ALC-4564). */
  VENDOR_RULE_DIVERGENCE_OPEN: 'VENDOR_RULE_DIVERGENCE_OPEN',
  /** bill_updated: the bill has more lines than a line-item rule may evaluate (B2). */
  LINE_CAP_EXCEEDED: 'LINE_CAP_EXCEEDED',
  /** Bank action: the action's config does not parse. */
  BANK_ACTION_CONFIG_INVALID: 'BANK_ACTION_CONFIG_INVALID',
  /** Bank action: a person chose the bill's origin bank account; the rule leaves it alone. */
  BANK_USER_PREFERENCE: 'BANK_USER_PREFERENCE',
  /** Bank action: the origin bank account came from a source the rule may not overwrite. */
  BANK_PROTECTED_PREFERENCE: 'BANK_PROTECTED_PREFERENCE',
  /** Bank action: card bills have no origin bank account. */
  BANK_CARD_PAYMENT_METHOD: 'BANK_CARD_PAYMENT_METHOD',
  /** Bank action: the rule's bank account is inactive, deleted or from another entity. */
  BANK_ACCOUNT_UNAVAILABLE: 'BANK_ACCOUNT_UNAVAILABLE',
  /** Reimbursement policy: no workflow matched. */
  REIMBURSEMENT_NO_MATCH: 'REIMBURSEMENT_NO_MATCH',
  /** Reimbursement policy: the entity suggestion fired on a trigger the policy does not run on. */
  REIMBURSEMENT_WRONG_TRIGGER: 'REIMBURSEMENT_WRONG_TRIGGER',
  /** Reimbursement policy: an entity suggestion is available and needs a user decision first;
   * auto-approval is deferred, not refused. */
  REIMBURSEMENT_ENTITY_SUGGESTION_PENDING: 'REIMBURSEMENT_ENTITY_SUGGESTION_PENDING',
  /** Reimbursement policy: the matched workflow has no auto-approve action. */
  REIMBURSEMENT_NO_AUTO_APPROVE_ACTION: 'REIMBURSEMENT_NO_AUTO_APPROVE_ACTION',
  /** Purchase request policy: no workflow matched. */
  PURCHASE_REQUEST_NO_MATCH: 'PURCHASE_REQUEST_NO_MATCH',
  /** Purchase request policy: the matched workflow has no approval action. */
  PURCHASE_REQUEST_NO_APPROVAL_ACTION: 'PURCHASE_REQUEST_NO_APPROVAL_ACTION',
  /** require_purchase_order action: the bill has no linked purchase order. */
  PURCHASE_ORDER_MISSING: 'PURCHASE_ORDER_MISSING',
  /** Condition evaluator: an unknown field, unsupported operator, or missing operator in a legacy
   * row's condition. One row per (workflowId, workflowVersionAt), never per evaluation. */
  CONDITION_INVALID: 'CONDITION_INVALID',
  /** ALC-4565 D37: the auto-approve rule's last changer has no authority covering the rule or this
   * bill (details: lastChangerUserId, tokenSource when updatedBy is NULL, failed, ceiling,
   * authorityMax). The bill goes to the next matching rule or the default chain. */
  AUTO_APPROVE_AUTHORITY_INSUFFICIENT: 'AUTO_APPROVE_AUTHORITY_INSUFFICIENT',
  /** ALC-4565 D51: the chain the bill would get without this rule needs more than one approval. */
  AUTO_APPROVE_CHAIN_NEEDS_MORE_APPROVERS: 'AUTO_APPROVE_CHAIN_NEEDS_MORE_APPROVERS',
  /** ALC-4565 D51 (P59): the rule's last changer is not an approver of that one-approval chain. */
  AUTO_APPROVE_NOT_CHAIN_APPROVER: 'AUTO_APPROVE_NOT_CHAIN_APPROVER',
  /** ALC-4565 fix r1 (F1, segregation of duties): the rule's last changer keyed in this bill
   * (`createdByUserId`) or submitted it by hand (`submittedByUserId`, a human). A person never
   * approves their own bill through a rule, whatever the tenant's `segregationEnabled` setting
   * (details: lastChangerUserId, createdByUserId, submittedByUserId, matchedOn). */
  AUTO_APPROVE_SAVER_IS_REQUESTER: 'AUTO_APPROVE_SAVER_IS_REQUESTER',
} as const;
export type WorkflowRunReasonCode =
  (typeof WorkflowRunReasonCode)[keyof typeof WorkflowRunReasonCode];

/** pt-BR + en copy for {@link WorkflowRunReasonCode}, for the ALC-4621 read API. */
export const WORKFLOW_RUN_REASON_COPY: Record<
  WorkflowRunReasonCode,
  { en: string; 'pt-BR': string }
> = {
  [WorkflowRunReasonCode.CLASSIFICATION_PROTECTED]: {
    en: 'The classification is protected from automation.',
    'pt-BR': 'A classificação está protegida contra automação.',
  },
  [WorkflowRunReasonCode.AUTO_APPROVE_SUPPRESSED]: {
    en: 'Automatic approval did not run: the bill is over budget and needs a budget exception approved first.',
    'pt-BR':
      'A aprovação automática não foi executada: a conta excede o orçamento e precisa de uma exceção de orçamento aprovada antes.',
  },
  [WorkflowRunReasonCode.CASCADE_NOT_SETTLED]: {
    en: 'The rules have not settled yet.',
    'pt-BR': 'As regras não se estabilizaram.',
  },
  [WorkflowRunReasonCode.PAYMENT_GAP]: {
    en: 'A payment gap blocks this transition.',
    'pt-BR': 'Uma lacuna de pagamento impede esta transição.',
  },
  [WorkflowRunReasonCode.STATUS_TRANSITION_FAILED]: {
    en: 'The bill status transition failed.',
    'pt-BR': 'A transição de status da conta falhou.',
  },
  [WorkflowRunReasonCode.BUDGET_HARD_BLOCK]: {
    en: 'The budget blocked this bill: it is over budget and no exception is allowed.',
    'pt-BR': 'O orçamento bloqueou esta conta: ela excede o orçamento e não há exceção permitida.',
  },
  [WorkflowRunReasonCode.VENDOR_RULE_DIVERGENCE_OPEN]: {
    en: 'The bill has an open vendor rule divergence. Resolve it before submitting for approval.',
    'pt-BR':
      'A conta tem uma divergência de regra do fornecedor em aberto. Resolva-a antes de enviar para aprovação.',
  },
  [WorkflowRunReasonCode.LINE_CAP_EXCEEDED]: {
    en: 'The bill has more lines than a line-item rule may evaluate.',
    'pt-BR': 'A conta tem mais itens do que uma regra de item pode avaliar.',
  },
  [WorkflowRunReasonCode.BANK_ACTION_CONFIG_INVALID]: {
    en: "The rule's bank account action is not configured correctly.",
    'pt-BR': 'A ação de conta bancária da regra não está configurada corretamente.',
  },
  [WorkflowRunReasonCode.BANK_USER_PREFERENCE]: {
    en: 'A person chose the origin bank account; the rule did not change it.',
    'pt-BR': 'Uma pessoa escolheu a conta bancária de origem; a regra não a alterou.',
  },
  [WorkflowRunReasonCode.BANK_PROTECTED_PREFERENCE]: {
    en: 'The origin bank account is protected from rules.',
    'pt-BR': 'A conta bancária de origem está protegida contra regras.',
  },
  [WorkflowRunReasonCode.BANK_CARD_PAYMENT_METHOD]: {
    en: 'Card bills have no origin bank account.',
    'pt-BR': 'Contas pagas com cartão não têm conta bancária de origem.',
  },
  [WorkflowRunReasonCode.BANK_ACCOUNT_UNAVAILABLE]: {
    en: "The rule's bank account is inactive or belongs to another entity.",
    'pt-BR': 'A conta bancária da regra está inativa ou pertence a outra empresa.',
  },
  [WorkflowRunReasonCode.REIMBURSEMENT_NO_MATCH]: {
    en: 'No reimbursement policy matched.',
    'pt-BR': 'Nenhuma política de reembolso correspondeu.',
  },
  [WorkflowRunReasonCode.REIMBURSEMENT_WRONG_TRIGGER]: {
    en: 'The reimbursement policy does not run on this trigger.',
    'pt-BR': 'A política de reembolso não é executada neste gatilho.',
  },
  [WorkflowRunReasonCode.REIMBURSEMENT_ENTITY_SUGGESTION_PENDING]: {
    en: 'An entity suggestion is available and needs a decision before auto-approval can run.',
    'pt-BR':
      'Há uma sugestão de entidade disponível que precisa de uma decisão antes da aprovação automática.',
  },
  [WorkflowRunReasonCode.REIMBURSEMENT_NO_AUTO_APPROVE_ACTION]: {
    en: 'The matched reimbursement policy has no auto-approve action.',
    'pt-BR': 'A política de reembolso correspondida não tem ação de aprovação automática.',
  },
  [WorkflowRunReasonCode.PURCHASE_REQUEST_NO_MATCH]: {
    en: 'No purchase request policy matched.',
    'pt-BR': 'Nenhuma política de solicitação de compra correspondeu.',
  },
  [WorkflowRunReasonCode.PURCHASE_REQUEST_NO_APPROVAL_ACTION]: {
    en: 'The matched workflow has no approval action.',
    'pt-BR': 'O fluxo correspondido não tem ação de aprovação.',
  },
  [WorkflowRunReasonCode.PURCHASE_ORDER_MISSING]: {
    en: 'The bill has no linked purchase order.',
    'pt-BR': 'A conta não tem pedido de compra vinculado.',
  },
  [WorkflowRunReasonCode.CONDITION_INVALID]: {
    en: 'This rule version has an invalid condition (unknown field or operator).',
    'pt-BR': 'Esta versão da regra tem uma condição inválida (campo ou operador desconhecido).',
  },
  [WorkflowRunReasonCode.AUTO_APPROVE_AUTHORITY_INSUFFICIENT]: {
    en: 'Rule skipped: the authority of the person who last changed it does not cover this bill.',
    'pt-BR': 'Regra pulada: a alçada de quem alterou a regra por último não cobre esta conta.',
  },
  [WorkflowRunReasonCode.AUTO_APPROVE_CHAIN_NEEDS_MORE_APPROVERS]: {
    en: 'Rule skipped: the chain has more than one approver.',
    'pt-BR': 'Regra pulada: a cadeia tem mais de um aprovador.',
  },
  [WorkflowRunReasonCode.AUTO_APPROVE_NOT_CHAIN_APPROVER]: {
    en: 'Rule skipped: the person who last changed it is not an approver of this chain.',
    'pt-BR': 'Regra pulada: quem alterou a regra por último não é aprovador desta cadeia.',
  },
  [WorkflowRunReasonCode.AUTO_APPROVE_SAVER_IS_REQUESTER]: {
    en: 'Rule skipped: the person who last changed it keyed in or submitted this bill and cannot approve it.',
    'pt-BR':
      'Regra pulada: quem alterou a regra por último lançou ou enviou esta conta e não pode aprová-la.',
  },
};

/** The two next steps {@link AUTO_APPROVE_SKIP_NEXT_STEP_COPY} words. */
export type AutoApproveSkipNextStep = 'CHAIN' | 'NO_CHAIN';
/**
 * One line of copy in both languages. A named type, not an inline object type: the web unit
 * runner pre-bundles this file and its module lexer cannot read an object type literal inside a
 * `satisfies` clause (it stopped every web unit test from starting).
 */
interface WorkflowLocalizedCopy {
  en: string;
  'pt-BR': string;
}

/**
 * ALC-4565 fix r1 (F7): the next-step sentence the skipped-rule history line appends to the
 * reason's copy. `CHAIN`: a later rule with an approval request or the default chain exists, so the
 * bill goes on. `NO_CHAIN`: neither exists, so no chain is built for this bill.
 */
export const AUTO_APPROVE_SKIP_NEXT_STEP_COPY = {
  CHAIN: {
    en: 'The bill goes on to the next rule or to the approvers.',
    'pt-BR': 'A conta segue para a próxima regra ou para os aprovadores.',
  },
  NO_CHAIN: {
    en: 'No approval chain was found for this bill: set a default chain or an approval rule.',
    'pt-BR':
      'Nenhuma cadeia de aprovação foi encontrada para esta conta: configure uma cadeia padrão ou uma regra de aprovação.',
  },
} as const satisfies Record<AutoApproveSkipNextStep, WorkflowLocalizedCopy>;

/**
 * pt-BR + en copy for the two outcomes design §2/§4 give explicit read-API wording for, for the
 * ALC-4621 read API: MATCHED ("conditions matched — actions run in the cascade", the literal
 * design-doc string) and RUNNING ("interrupted" — finalize never ran after a committed action, or
 * never ran at all). The other outcomes (DONE, SKIPPED, REFUSED, FAILED) have no copy requirement
 * of their own here — the outcome value itself, plus `reasonCode`'s copy, carries the meaning.
 */
export const WORKFLOW_RUN_OUTCOME_COPY: Partial<
  Record<WorkflowRunOutcome, { en: string; 'pt-BR': string }>
> = {
  [WorkflowRunOutcome.MATCHED]: {
    en: 'Conditions matched — actions run in the cascade.',
    'pt-BR': 'Condições correspondidas — as ações são executadas na cascata.',
  },
  [WorkflowRunOutcome.RUNNING]: {
    en: 'Interrupted — this run never finished recording its result.',
    'pt-BR': 'Interrompida — esta execução nunca terminou de registrar seu resultado.',
  },
};
